import React from 'react';
import PropTypes from 'prop-types';
import { styled, alpha } from '@mui/material/styles';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { CampaignIndicator } from '.';
import { LIFECYCLE_STATUS_MAP } from 'common/constants/lifecycle-and-health-mappings';

const Root = styled('div')(({ theme }) => {
    const base = theme.palette.info.main;
    return {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 12,
        padding: '4px 12px',
        borderRadius: 4,
        backgroundColor: alpha(base, 0.1),
        border: `1px solid ${alpha(base, 0.1)}`,
    };
});

const TextWrap = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    minWidth: 240,
});

function LearnMore({ href, onClick }) {
    if (href) {
        return (
            <Link
                href={href}
                underline="hover"
                sx={{ fontWeight: 700 }}
                target="_blank"
                rel="noopener"
            >
                Learn More
            </Link>
        );
    }
    if (onClick) {
        return (
            <Link component="button" underline="hover" onClick={onClick} sx={{ fontWeight: 700 }}>
                Learn More
            </Link>
        );
    }
    return null;
}

export const CampaignIndicatorWithAlert = React.memo(function CampaignIndicatorWithAlert({
    status,
    size = 'small',
    description,
    learnMoreHref,
    onLearnMore,
    className,
    style,
}) {
    const spec = LIFECYCLE_STATUS_MAP[status] || {};
    const desc = description ?? spec.description ?? '';

    return (
        <Root className={className} style={style}>
            <CampaignIndicator status={status} size={size} showHealth={false} />
            <TextWrap>
                {desc ? (
                    <Typography
                        component="span"
                        variant={size === 'small' ? 'body2' : 'body1'}
                        sx={{ color: 'text.primary' }}
                    >
                        {desc}
                    </Typography>
                ) : null}
                <LearnMore href={learnMoreHref} onClick={onLearnMore} />
            </TextWrap>
        </Root>
    );
});

CampaignIndicatorWithAlert.propTypes = {
    status: PropTypes.oneOf([...Object.keys(LIFECYCLE_STATUS_MAP)]).isRequired,
    size: PropTypes.oneOf(['small', 'medium']),
    description: PropTypes.string,
    learnMoreHref: PropTypes.string,
    onLearnMore: PropTypes.func,
    className: PropTypes.string,
    style: PropTypes.object,
};

export default CampaignIndicatorWithAlert;
